import moment from 'moment';
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import LoaderComponent from '@components/LoaderComponent';
import { toastSuccess, toastWarning } from '@components/Toast';

import AddNewTaskModal from '@features/order/components/TNA/AddNewTaskModal';
import CreateDuplicateTNA from '@features/order/components/TNA/CreateDuplicateTNA';
import DuplicateTnaConfirmModal from '@features/order/components/TNA/DuplicateTnaConfirmModal';
import EtdDetails from '@features/order/components/TNA/EtdDetails';
import SetProductsTabButtons from '@features/order/components/TNA/SetProductsTabButtons';
import TnaStylesList from '@features/order/components/TNA/TnaStylesList';
import TNAAccordian from '@features/order/components/TNA/TNAAccordian';
import {
	CREATE_TASK_MODAL_REQUIRED_FIELDS,
	GROUP_BY_TASK_LIST,
	INITIAL_TASK_MODAL_STATE,
	isPlaningPending,
	isProductOfSet,
	isSetProduct,
	PLANNING_STATUS,
	SUB_TASK_LABEL,
	TNA_REQUEST_TYPE
} from '@features/order/utils/projectUtil';

import tnaService from '@services/tna-service';

import { printApiError, STATIC_SET_CONSTANTS } from '@utils/Util';

// lodash groupBy returns a plain object, and JavaScript iterates integer-like keys in
// ascending numeric order rather than insertion order. Grouping the plan by deliverableId
// therefore sorted the table by id instead of by position, which put any deliverable added
// later - its id being higher than the originally seeded 1-26 - at the bottom regardless of
// its place in the plan. The backend already returns the tasks in step sequence, so a Map
// keyed the same way preserves that order whatever the ids happen to be.
const groupTasksByDeliverable = (taskList = []) => {
	const groups = new Map();
	(taskList || []).forEach((task) => {
		const key = task?.[GROUP_BY_TASK_LIST];
		if (!groups.has(key)) {
			groups.set(key, { [GROUP_BY_TASK_LIST]: key, [SUB_TASK_LABEL]: [] });
		}
		groups.get(key)[SUB_TASK_LABEL].push(task);
	});
	return Array.from(groups.values());
};

const CreateTna = ({
	orderId,
	isShowProducts = true,
	planningStatus,
	rfqId,
	callback,
	getProductList,
	requestFrom,
	tnaRunningStyleStatus,
	selectedStyleDetailsFromRunning,
	runningProductList,
	setProductList,
	preselectRfqId
}) => {
	const [loading, setLoading] = useState(false);
	const [showModal, setShowModal] = useState(false);
	const [tnaProductList, setTnaProductList] = useState([]);
	const [selectedRfqId, setSelectedRfqId] = useState(0);
	const [selectedStyle, setSelectedStyle] = useState(null);
	const [selectedTemplateDays, setSelectedTemplateDays] = useState(null);
	const [stageTaskList, setStageTaskList] = useState([]);
	const [taskFormInputData, setTaskFormInputData] = useState(INITIAL_TASK_MODAL_STATE);
	const navigate = useNavigate();
	const [activeSetMemberId, setActiveSetMemberId] = useState(null);
	const [productsOfSet, setProductsOfSet] = useState([]);
	const [isFinishTabVisible, setIsFinishTabVisible] = useState(false);
	const [isTnaHasSetProduct, setIsTnaHasSetProduct] = useState(false);
	const [submittedSetMembers, setSubmittedSetMembers] = useState([]);
	const [selectedProductId, setSelectedProductId] = useState(null);
	const [errors, setErrors] = useState({});
	// A draft template opens with an empty Due date column; this holds the dates the planner
	// has actually chosen, keyed by task id.
	const [userSetDueDates, setUserSetDueDates] = useState({});
	// Set once the planner clears the dates, so the ones the plan arrived with stop being
	// shown too. Cleared again when another style is opened, which brings its own dates.
	const [datesCleared, setDatesCleared] = useState(false);
	const [selectedStyleDetails, setSelectedStyleDetails] = useState(null);
	const [isSelecteButtonClicked, setIsSelectedButtonClicked] = useState(false);
	const [isUsingExcel, setIsUsingExcel] = useState(false);
	const [isCompleated, setIsCompleated] = useState(false);
	const [listOfAllTnaProduct, setListOfAllTnaProduct] = useState([]);
	const [rfqStatusInfoList, setRfqStatusInfoList] = useState([]);
	const [selectedETD, setSelectedETD] = useState(0);
	const [selectedProduct, setSelectedProduct] = useState(null);
	// The style whose TNA is about to be copied, held while the planner is asked whether its dates
	// should come across too.
	const [planToDuplicate, setPlanToDuplicate] = useState(null);
	//const [selectedIndex, setSelectedIndex] = useState(0);

	useEffect(() => {
		//set listOfAllTnaProduct[0].rfqStatusInfoList to setRfqStatusInfoList
		if (listOfAllTnaProduct.length > 0) {
			setRfqStatusInfoList(listOfAllTnaProduct[0].rfqStatusInfoList);
		}
	}, [listOfAllTnaProduct]);
	useEffect(() => {
		//set listOfAllTnaProduct[0].rfqStatusInfoList to setRfqStatusInfoList
		if (listOfAllTnaProduct.length > 0) {
			setSelectedProduct(listOfAllTnaProduct[0]);
		}
	}, [listOfAllTnaProduct]);

	useEffect(() => {
		setListOfAllTnaProduct(tnaProductList);
	}, [JSON.stringify(tnaProductList)]);

	useEffect(() => {
		if (selectedStyleDetailsFromRunning) {
			setSelectedStyleDetails(selectedStyleDetailsFromRunning);
		}
	}, [selectedStyleDetailsFromRunning?.rfqId]);

	// A style with no saved plan is reported by the backend as EXCEL_FILE, which used to leave the
	// whole table read-only until a template was picked by hand. There is no template picker any
	// more, so such a style opens on its default template instead. A style that really was planned
	// from a spreadsheet keeps its own source.
	const getDefaultPlanningSource = (product) =>
		product?.planningStatus === PLANNING_STATUS.PENDING
			? TNA_REQUEST_TYPE.TEMPLATE
			: product?.planningSource;

	const handleTnaSelection = (product, requestType) => {
		const planningSource = requestType || getDefaultPlanningSource(product);
		if (isProductOfSet(product) && product.setMemberList && product.setMemberList.length > 0) {
			const firstSetMemberRfqId = product?.setMemberList[0]?.id;
			getSelectedTna(product?.rfqId, '', firstSetMemberRfqId, planningSource);
		} else {
			getSelectedTna(product?.rfqId, '', '', planningSource);
		}
	};

	// Fetch all the TNA product list and add default slected product (*styles list)
	const getAllProductList = async (shouldSelect = true) => {
		const tnaProductListResponse = await tnaService.getAllTnaProductList(orderId);
		setTnaProductList(tnaProductListResponse);

		// Opened from the order dashboard for one style, so that style is landed on instead of the default pick.
		const preselectedProduct =
			preselectRfqId &&
			tnaProductListResponse.find((product) => String(product?.rfqId) === String(preselectRfqId));

		let initialSelectedProduct =
			preselectedProduct ||
			tnaProductListResponse.find((product) => product?.planningStatus === PLANNING_STATUS.PENDING);
		if (shouldSelect) {
			if (initialSelectedProduct) {
				handleTnaSelection(initialSelectedProduct);
			} else {
				handleTnaSelection(tnaProductListResponse[0]);
			}
		}
	};

	const getSelectedTna = async (rfqId, templateName = '', idOfSetMember, planningSource) => {
		try {
			setLoading(true);
			// Dates the planner picked belong to the style they were picked on.
			setUserSetDueDates({});
			setDatesCleared(false);
			const selectedTnaResponse = await tnaService.selectedTna(
				orderId,
				rfqId,
				templateName,
				idOfSetMember,
				planningSource
			);
			setSelectedRfqId(rfqId);
			setSelectedStyle(selectedTnaResponse);
		} catch (error) {
			printApiError(error);
		} finally {
			setLoading(false);
		}
	};

	const isExcelFileSubmitted = () => {
		return (
			selectedStyle?.planningSource === TNA_REQUEST_TYPE.EXCEL_FILE &&
			isCompleated &&
			selectedStyle?.rfqId === selectedStyleDetails?.rfqId
		);
	};

	useEffect(() => {
		if (selectedStyle?.planningSource === TNA_REQUEST_TYPE.EXCEL_FILE) {
			setIsUsingExcel(true);
			setSelectedTemplateDays(null);
		} else {
			setIsUsingExcel(false);
			setSelectedTemplateDays({
				templateDurationDays: selectedStyle?.timeOfCompletion,
				templateName: selectedStyle?.templateName
			});
		}
		if (
			selectedStyle?.status === PLANNING_STATUS.COMPLETED ||
			selectedStyle?.status === PLANNING_STATUS.SUBMITTED
		) {
			setIsCompleated(true);
		} else {
			setIsCompleated(false);
		}
	}, [selectedStyle]);

	useEffect(() => {
		setLoading(true);
		if (requestFrom !== 'TNA_RUNNING') getAllProductList();
		setLoading(false);
	}, [requestFrom]);

	useEffect(() => {
		if (
			planningStatus === PLANNING_STATUS.PENDING ||
			(planningStatus === PLANNING_STATUS.SUBMITTED && rfqId && requestFrom === 'TNA_RUNNING')
		) {
			onSelectProduct(selectedStyleDetailsFromRunning, rfqId);
		}
	}, [rfqId, requestFrom]);

	const getInitialSetProduct = () => {
		if (
			(tnaProductList?.length > 0 && selectedRfqId) ||
			(runningProductList?.length > 0 && selectedStyleDetailsFromRunning?.rfqId)
		) {
			const productList = requestFrom === 'TNA_RUNNING' ? runningProductList : tnaProductList;

			const isRequestFromRunning = (product) => {
				if (requestFrom === 'TNA_RUNNING') {
					return selectedStyleDetailsFromRunning?.rfqId === product?.rfqId;
				} else {
					return product?.rfqId === selectedRfqId;
				}
			};

			const selectedSetProduct = productList.find(
				(product) => isRequestFromRunning(product) && isProductOfSet(product)
			);

			if (selectedSetProduct) {
				extractAndOrganizeSetMembers([selectedSetProduct]);
				setIsTnaHasSetProduct(true);
			} else {
				extractAndOrganizeSetMembers([]);
				setActiveSetMemberId(null);
				setIsTnaHasSetProduct(false);
			}
		}
	};

	useEffect(() => {
		getInitialSetProduct();
	}, [selectedRfqId, tnaProductList, selectedStyleDetailsFromRunning?.rfqId, runningProductList]);

	const isEveryProductOfSetSubmitted = () => {
		let selectedItem = tnaProductList?.find(
			(item) => item.rfqId === selectedRfqId || item?.rfqId === selectedStyleDetailsFromRunning?.rfqId
		);
		let tmpIsEveryMemberSubmitted = false;
		if (selectedItem && isProductOfSet(selectedItem)) {
			if (selectedItem.setMemberList && selectedItem.setMemberList.length > 0) {
				tmpIsEveryMemberSubmitted = selectedItem.setMemberList.every(
					(item) => item?.planningStatus !== PLANNING_STATUS.PENDING
				);
			}
		}
		return tmpIsEveryMemberSubmitted;
	};

	useEffect(() => {
		setActiveSetMemberId(productsOfSet[0]?.id);
	}, [selectedProductId, selectedStyleDetails?.rfqId, selectedStyleDetailsFromRunning?.rfqId]);

	const onSetStyleDetails = () => {
		let selectedItem = tnaProductList?.find((item) => item.rfqId === selectedRfqId);
		setSelectedStyleDetails(selectedItem);
		setSelectedProductId(selectedItem?.id);
	};

	useEffect(() => {
		if (tnaProductList?.length > 0 && selectedRfqId) {
			onSetStyleDetails();
		}
	}, [selectedRfqId, tnaProductList]);

	const extractAndOrganizeSetMembers = (designsList = []) => {
		const membersOfSet = [];
		const compleatedTna = [];
		designsList.forEach((design) => {
			if (design.setPackStyleFlag === STATIC_SET_CONSTANTS.SET_PRODUCT && design.setMemberList) {
				design.setMemberList.forEach((member) => {
					membersOfSet.push({
						...member
					});
					if (member?.planningStatus !== PLANNING_STATUS.PENDING) {
						compleatedTna.push(member?.id);
					}
				});
				membersOfSet.push({
					name: 'Finish',
					id: design?.id,
					rfqId: design?.rfqId,
					planningStatus: design?.planningStatus
				});
				if (design?.planningStatus !== PLANNING_STATUS.PENDING) {
					compleatedTna.push(design?.id);
				}
			}
		});
		setSubmittedSetMembers(compleatedTna);
		setProductsOfSet(membersOfSet);
	};

	// Kept off the critical path: the style cards only carry planning status and the style's
	// own dates, neither of which a task edit changes, so the planner is not made to wait.
	const refreshTnaProductList = () => {
		tnaService
			.getAllTnaProductList(orderId)
			.then((response) => response && setTnaProductList(response))
			.catch(() => {
				/* the list is refreshed again on the next load; nothing to report here */
			});
	};

	const updatingNotApplicable = async (body) => {
		setLoading(true);
		let updatedDurationResponse;
		if (activeSetMemberId && !isFinishTabVisible) {
			updatedDurationResponse = await tnaService.updateNotApplicable(
				orderId,
				selectedRfqId,
				body,
				activeSetMemberId
			);
		} else {
			updatedDurationResponse = await tnaService.updateNotApplicable(orderId, selectedRfqId, body);
		}

		setSelectedStyle(updatedDurationResponse);
		refreshTnaProductList();
		setLoading(false);
	};

	const updateTask = async ({ taskName, deliverableName, dueDate }) => {
		setLoading(true);
		const reqBody = {
			name: taskName,
			deliverableId: deliverableName,
			deliveryDate: dueDate
		};
		try {
			let updateTnaTaskResponse;
			if (activeSetMemberId) {
				updateTnaTaskResponse = await tnaService.updateTask(
					orderId,
					selectedRfqId,
					reqBody,
					activeSetMemberId
				);
			} else {
				updateTnaTaskResponse = await tnaService.updateTask(orderId, selectedRfqId, reqBody);
			}

			const tnaProductListResponse = await tnaService.getAllTnaProductList(orderId);
			setTnaProductList(tnaProductListResponse);
			setTaskFormInputData(INITIAL_TASK_MODAL_STATE);
			setSelectedStyle({
				...selectedStyle,
				taskPlanningResponseList: updateTnaTaskResponse?.taskPlanningResponseList
			});
			setLoading(false);
		} catch {
			setLoading(false);
		}
	};

	const getPendingProductList = (productList = []) => {
		const pendingProductList = productList.filter((item) => isPlaningPending(item));
		return pendingProductList;
	};

	const createTna = async (requiredCallback = true) => {
		let queryParams = `?planningSource=${
			selectedTemplateDays ? TNA_REQUEST_TYPE.TEMPLATE : TNA_REQUEST_TYPE.EXCEL_FILE
		}`;

		if (productsOfSet.length > 0) {
			queryParams += `&setMemberIds=${productsOfSet
				.slice(0, 2)
				.map((item) => item.id)
				.join(',')}`;
		}

		await tnaService.submitIndividualTna(
			orderId,
			requestFrom === 'TNA_RUNNING' ? rfqId : selectedRfqId,
			queryParams
		);
		if (callback && requiredCallback) {
			callback(rfqId);
		}
	};

	const updatedTnaSubmit = async () => {
		const tasksMissingDueDate = (selectedStyle?.taskPlanningResponseList || []).filter(
			(task) => !task?.notApplicable && !getDueDateToShow(task)
		);
		if (tasksMissingDueDate.length > 0) {
			toastWarning(
				`Give every selected task a due date first — ${tasksMissingDueDate.length} still ` +
					`without one, starting with "${tasksMissingDueDate[0]?.stepDescription}".`
			);
			return;
		}

		const tasksOutOfRange = (selectedStyle?.taskPlanningResponseList || []).filter(isDueDateOutOfRange);
		if (tasksOutOfRange.length > 0) {
			toastWarning(
				`All tasks must be completed within factory handover date and factory ETD — ` +
					`${tasksOutOfRange.length} marked in red, starting with ` +
					`"${tasksOutOfRange[0]?.stepDescription}".`
			);
			return;
		}

		try {
			setLoading(true);
			// Every date the planner can see in the Due date column is sent, so the plan is saved
			// with exactly those - the ones they typed and the ones a duplicated plan arrived with.
			const taskDueDates = (selectedStyle?.taskPlanningResponseList || [])
				.filter((task) => !task?.notApplicable && getDueDateToShow(task))
				.map((task) => ({
					id: task?.id,
					deliveryDate: getDueDateToShow(task)
				}));

			let submitTnaResponse = await tnaService.submitTna(
				orderId,
				selectedRfqId,
				activeSetMemberId && !isFinishTabVisible ? activeSetMemberId : null,
				taskDueDates
			);

			if (setProductList && requestFrom === 'TNA_RUNNING') {
				if (productsOfSet.length > 0) {
					let modifiedRunningProductList = runningProductList?.map((item) => {
						if (item.rfqId === selectedRfqId) {
							return {
								...item,
								setMemberList: item?.setMemberList?.map((member) => {
									if (member.id === activeSetMemberId) {
										return {
											...member,
											planningStatus: submitTnaResponse?.status || member.planningStatus
										};
									}
									return member;
								})
							};
						}
						return item;
					});
					setProductList(modifiedRunningProductList);
				}
			}

			setSubmittedSetMembers([...submittedSetMembers, activeSetMemberId]);
			if (submitTnaResponse) {
				const tnaProductListResponse = await tnaService.getAllTnaProductList(orderId);

				if (
					requestFrom === 'TNA_RUNNING' &&
					((productsOfSet.length > 0 && isFinishTabVisible) || productsOfSet.length === 0)
				) {
					await createTna();
				}

				setTnaProductList(tnaProductListResponse);

				const activeSet = tnaProductListResponse?.find((item) => item.rfqId === selectedRfqId);

				if (isTnaHasSetProduct && isPlaningPending(activeSet)) {
					setLoading(false);
					return;
				}

				const pendingProductList = getPendingProductList(tnaProductListResponse);

				if (pendingProductList.length > 0) {
					const firstPendingProductRfqId = pendingProductList[0].rfqId;
					if (!isProductOfSet(pendingProductList[0])) {
						getSelectedTna(
							firstPendingProductRfqId,
							'',
							'',
							pendingProductList[0]?.planningSource
						);
					} else if (isProductOfSet(pendingProductList[0])) {
						getSelectedTna(
							firstPendingProductRfqId,
							'',
							pendingProductList[0]?.setMemberList[0]?.id,
							pendingProductList[0]?.planningSource
						);
					} else if (isFinishTabVisible) {
						getSelectedTna(
							firstPendingProductRfqId,
							'',
							'',
							pendingProductList[0]?.planningSource
						);
					}
					window.scrollTo(0, 0);
				}
			}
		} catch (error) {
			printApiError(error);
		} finally {
			setLoading(false);
		}
	};

	const onSelectProduct = (product, rfqIdFromRunningTna, templateDays) => {
		setSelectedProduct(product);
		setLoading(true);
		setRfqStatusInfoList(product?.rfqStatusInfoList);
		const planningSource = getDefaultPlanningSource(product);
		if (rfqIdFromRunningTna && isProductOfSet(product)) {
			getSelectedTna(rfqIdFromRunningTna, '', product?.setMemberList[0]?.id, planningSource);
			return;
		} else if (rfqIdFromRunningTna && !isProductOfSet(product)) {
			getSelectedTna(rfqIdFromRunningTna, '', '', planningSource);
			return;
		}
		if (isProductOfSet(product)) {
			getSelectedTna(product?.rfqId, '', product?.setMemberList[0]?.id, planningSource);
		} else {
			getSelectedTna(product?.rfqId, templateDays, '', planningSource);
		}
	};

	// Picking a style only raises the question; nothing is copied until the planner has said
	// whether the dates come with the tasks.
	const onSelectPlanForDuplication = (plan) => setPlanToDuplicate(plan);

	const onConfirmDuplication = async (withDueDates) => {
		const plan = planToDuplicate;
		setPlanToDuplicate(null);
		if (!plan) return;

		setLoading(true);
		const tnaResponse = await tnaService.duplicateTask(
			orderId,
			selectedRfqId,
			plan?.rfqId,
			activeSetMemberId ? activeSetMemberId : selectedProductId,
			plan?.productId,
			requestFrom === 'TNA_RUNNING' ? selectedStyleDetailsFromRunning?.id : '',
			plan?.orderId,
			withDueDates
		);

		// The source style can be on another order, so a copy can fail in ways the old in-order
		// list could not; the plan on screen is left as it was and the error has been reported.
		if (tnaResponse) {
			setSelectedRfqId(tnaResponse?.rfqId);
			setSelectedStyle(tnaResponse);
		}

		if (requestFrom === 'TNA_RUNNING') {
			await getProductList();
			await getAllProductList(false);
		} else {
			await getAllProductList();
		}
		setLoading(false);
	};

	// Nothing is shown until the planner picks a date, so the dates the template works out stay
	// out of the way. A plan that carries dates of its own shows them: one already submitted, one
	// that came from a spreadsheet, and one made by duplicating another style, which arrives with
	// the dates of the plan it was copied from.
	const getDueDateToShow = (subTask) => {
		if (subTask?.notApplicable) return null;

		if (userSetDueDates[subTask?.id]) return userSetDueDates[subTask?.id];
		if (datesCleared) return null;
		if (isCompleated || isUsingExcel || selectedStyle?.dueDatesGiven) return subTask?.endDate;
		return null;
	};

	// A date is held locally while the planner works through the list, and the whole set is
	// sent once on submit. Saving each one as it was typed meant a request per task, and the
	// server shifted every other date each time.
	// Enter and the down arrow move down the column. The down arrow would otherwise step the
	// day of the segment under the cursor, which is of little use when the date is typed out.
	const onDueDateKeyDown = (event) => {
		if (event.key !== 'Enter' && event.key !== 'ArrowDown') return;

		event.preventDefault();
		const fields = Array.from(document.querySelectorAll('input.tna-due-date:not([disabled])'));
		const next = fields[fields.indexOf(event.currentTarget) + 1];
		if (!next) return;

		next.focus();
		next.scrollIntoView({ block: 'nearest' });
	};

	const onChangeDueDate = (e) => {
		const { name, value } = e.target;
		if (!value) return;

		setUserSetDueDates((previous) => ({ ...previous, [name]: value }));
	};

	const onTaskCheckboxChange = (input) => {
		const { value } = input.target;
		const taskList = groupTasksByDeliverable(selectedStyle?.taskPlanningResponseList);

		setStageTaskList(taskList);
		let body = {
			deliveryDate: '',
			durationInDay: '',
			id: '',
			notApplicable: ''
		};

		taskList.forEach((stage) => {
			stage.subTask.forEach((task) => {
				if (task.id == value) {
					task.notApplicable = !task.notApplicable;
					body.id = task.id;
					body.deliveryDate = task.endDate;
					body.durationInDay = task.durationDays;
					body.notApplicable = task.notApplicable;
				}
			});
		});

		setStageTaskList(taskList);
		updatingNotApplicable(body);
	};

	const validateProperty = ({ name, value }) => {
		if (CREATE_TASK_MODAL_REQUIRED_FIELDS[name].required && !value) {
			return `${CREATE_TASK_MODAL_REQUIRED_FIELDS[name].label} is required`;
		}
		return null;
	};

	const handleAddTaskFormChange = (e) => {
		const { name, value } = e.target;
		const errorList = { ...errors };
		const errorMessage = validateProperty({ name, value });
		if (errorMessage) errorList[name] = errorMessage;
		else delete errorList[name];

		let selectedTaskOption;
		if (name === 'stepName' && value) {
			stageTaskList.forEach((stage) => {
				stage.subTask.forEach((task) => {
					if (task.id == value) {
						selectedTaskOption = task;
					}
				});
			});
			taskFormInputData.startDate = moment(selectedTaskOption?.endDate).add(1, 'days').format('D MMM');
			taskFormInputData.startDateOrg = moment(selectedTaskOption?.endDate).add(1, 'days');
			if (selectedTaskOption && taskFormInputData.duration)
				taskFormInputData.endDate = moment(taskFormInputData.startDateOrg)
					.add(taskFormInputData.duration, 'days')
					.format('D MMM');
		}

		if (name === 'duration' && value && taskFormInputData?.startDateOrg)
			taskFormInputData.endDate = moment(taskFormInputData.startDateOrg)
				.add(value, 'days')
				.format('D MMM');

		setTaskFormInputData({ ...taskFormInputData, [name]: value });
		setErrors(errorList);
	};

	const validate = (data) => {
		const errors = {};

		for (const field in CREATE_TASK_MODAL_REQUIRED_FIELDS) {
			if (CREATE_TASK_MODAL_REQUIRED_FIELDS[field].required && !data[field]) {
				errors[field] = `${CREATE_TASK_MODAL_REQUIRED_FIELDS[field].label} is required`;
			}
		}

		return Object.keys(errors).length === 0 ? null : errors;
	};

	const onSubmit = (e) => {
		e.preventDefault();
		const errors = validate(taskFormInputData);
		setErrors(errors);
		if (errors) return;

		updateTask(taskFormInputData);
		setShowModal(false);
	};

	const onModalCancel = () => {
		setShowModal(false);
		setTaskFormInputData(INITIAL_TASK_MODAL_STATE);
		setErrors({});
	};

	const onChangeTabOfSetMember = (member, index) => {
		if (member?.name === 'Finish') {
			if (!isEveryProductOfSetSubmitted()) {
				return;
			}
		}
		if (activeSetMemberId !== member.id) {
			if (productsOfSet.length - 1 === index) {
				getSelectedTna(
					member?.rfqId,
					selectedTemplateDays?.templateName ? selectedTemplateDays?.templateName : '',
					'',
					selectedTemplateDays?.templateName
						? TNA_REQUEST_TYPE.TEMPLATE
						: TNA_REQUEST_TYPE.EXCEL_FILE
				);
				if (requestFrom === 'TNA_RUNNING' && member.planningStatus === PLANNING_STATUS.COMPLETED) {
					getProductList();
				}
			} else {
				getSelectedTna(
					member?.rfqId,
					selectedTemplateDays?.templateName ? selectedTemplateDays?.templateName : '',
					member?.id,
					selectedTemplateDays?.templateName
						? TNA_REQUEST_TYPE.TEMPLATE
						: TNA_REQUEST_TYPE.EXCEL_FILE
				);
			}
			setLoading(true);
			setActiveSetMemberId(member?.id);
		}
	};
	const onSelectFinishTab = () => {
		if (productsOfSet.length > 0) {
			const getFinshTab = productsOfSet.find((item) => item.id === activeSetMemberId);
			if (getFinshTab && getFinshTab.name === 'Finish') {
				setIsFinishTabVisible(true);
			} else {
				setIsFinishTabVisible(false);
			}
		}
	};

	useEffect(() => {
		onSelectFinishTab();
	}, [activeSetMemberId]);

	// Ticking a task off and moving its due date are both only possible while the plan is still
	// being drafted from a template, and pre-production is owned by the base RFQ alone.
	const isTaskEditingLocked = (type) =>
		isUsingExcel ||
		isExcelFileSubmitted() ||
		tnaRunningStyleStatus?.planningStatus === PLANNING_STATUS.SUBMITTED ||
		(!selectedProduct?.isBaseRfq && type === 'PRE_PRODUCTION');

	// A task is out of range when its due date falls outside the factory handover date and
	// the factory ETD, which is the window the whole plan has to fit inside.
	const isDueDateOutOfRange = (task) => {
		if (task?.notApplicable) return false;

		const dueDate = getDueDateToShow(task);
		if (!dueDate) return false;

		const handoverDate = selectedStyleDetails?.factoryHandOverDate;
		const deliveryDate = selectedStyleDetails?.factoryDeliveryDate;

		return (
			(!!handoverDate && moment(dueDate).isBefore(moment(handoverDate), 'day')) ||
			(!!deliveryDate && moment(dueDate).isAfter(moment(deliveryDate), 'day'))
		);
	};

	const getClassNameForRow = (task) => (isDueDateOutOfRange(task) ? 'red' : '');

	const handleExcelFileUpload = async (e) => {
		const fileInput = e.target;
		try {
			setLoading(true);
			const files = Array.from(fileInput.files);
			const { data } = await tnaService.uploadTnaExcelFile(files, orderId);
			if (data?.success) {
				toastSuccess('File uploaded successfully!');
				window.scrollTo(0, 0);
				if (requestFrom === 'TNA_RUNNING') {
					await createTna();
					getProductList();
					handleTnaSelection(selectedStyleDetailsFromRunning);
				} else {
					getAllProductList();
				}
			}
		} catch {
			fileInput.value = '';
		} finally {
			fileInput.value = '';
			setLoading(false);
		}
	};

	// Clears the dates on this style and nothing else. The template, the ticks and the tasks
	// are left as they are and the plan stays editable, so the column can simply be filled in
	// again. A date the plan arrived with goes too - a duplicated plan carries the dates of
	// the style it was copied from - so the column reads empty whichever way the dates got
	// there.
	const handleClearDates = () => {
		setUserSetDueDates({});
		setDatesCleared(true);
	};

	const handleStartOrder = async () => {
		try {
			setLoading(true);
			await tnaService.onStartOrder(orderId);
			navigate('/orders/my-orders?active=RUNNING');
		} catch (error) {
			toastWarning(error?.response?.data?.message || 'Something went wrong! Please try again.');
		} finally {
			setLoading(false);
		}
	};

	// const handleDownloadFile = async () => {
	// 	setLoading(true);
	// 	//get rfqId from rfqStatusInfoList
	// 	const rfqIds = rfqStatusInfoList.map((item) => item.rfqId);
	// 	await tnaService
	// 		.downloadTnaExcelFile(orderId, requestFrom === 'TNA_RUNNING' ? rfqIds : selectedRfqId)
	// 		.finally(() => setLoading(false));
	// };
	const handleDownloadFile = async () => {
		setLoading(true);

		await tnaService.downloadTnaExcelFile(orderId, selectedRfqId).finally(() => setLoading(false));
	};

	const renderDeliverableName = (constants = [], type = '') => {
		let taskList;
		let sl = 0;
		if (selectedStyle?.taskPlanningResponseList) {
			const filteredTasks = constants.length
				? selectedStyle?.taskPlanningResponseList.filter((task) =>
						constants.includes(task.stageConstants)
					)
				: selectedStyle?.taskPlanningResponseList.filter(
						(task) =>
							!['ORDER_PLACED', 'MATERIALS', 'SAMPLING', 'PP_ACTIVITIES'].includes(
								task.stageConstants
							)
					);
			taskList = groupTasksByDeliverable(filteredTasks);
		}

		return (
			taskList &&
			taskList.map((task) => {
				return task.subTask.map((subTask) => {
					sl = sl + 1;
					let className = getClassNameForRow(subTask);
					return (
						<tr key={subTask?.id}>
							<td>{sl}</td>
							<td className="task-td">
								<div className="custom-chekbox d-inline-block align-middle">
									<div className="form-group m-0">
										<input
											type="checkbox"
											id={subTask?.id}
											onChange={onTaskCheckboxChange}
											value={subTask?.id}
											checked={!subTask?.notApplicable}
											disabled={isTaskEditingLocked(type)}
										/>
										<label htmlFor={subTask?.id}>
											{' '}
											<span className="regular-14">{subTask?.stepDescription}</span>
										</label>
									</div>
								</div>
							</td>
							<td className={`due-date-td ${className}`}>
								<div className="tna-due-date-wrap">
									{/* At rest the date reads as 26 Nov; focusing the cell reveals the real
									    field so what is being typed is visible. */}
									<span
										className={`tna-due-date-label ${
											getDueDateToShow(subTask) ? '' : 'is-empty'
										} ${isTaskEditingLocked(type) || subTask?.notApplicable ? 'is-locked' : ''}`}
									>
										{subTask?.notApplicable
											? '—'
											: getDueDateToShow(subTask)
												? moment(getDueDateToShow(subTask)).format('D MMM')
												: 'Set date'}
									</span>
									{!isTaskEditingLocked(type) && !subTask?.notApplicable && (
										<input
											type="date"
											className="tna-due-date"
											id={subTask?.id}
											name={subTask?.id}
											value={
												getDueDateToShow(subTask)
													? moment(getDueDateToShow(subTask)).format('YYYY-MM-DD')
													: ''
											}
											onChange={onChangeDueDate}
											onKeyDown={onDueDateKeyDown}
										/>
									)}
								</div>
							</td>
						</tr>
					);
				});
			})
		);
	};

	const renderClearButton = () => {
		const clearButton = () => {
			return (
				<button className="button gray-outline size32 ml-2" onClick={handleClearDates}>
					<span>Clear Dates</span>
				</button>
			);
		};
		if (isSetProduct(selectedStyleDetails)) {
			if (
				!isExcelFileSubmitted() &&
				selectedTemplateDays &&
				productsOfSet[0]?.planningStatus === PLANNING_STATUS.PENDING &&
				activeSetMemberId === productsOfSet[0]?.id
			) {
				return clearButton();
			} else return null;
		} else {
			if (
				!isExcelFileSubmitted() &&
				selectedTemplateDays &&
				selectedStyleDetails?.planningStatus === PLANNING_STATUS.PENDING
			) {
				return clearButton();
			}
		}
	};
	useEffect(() => {
		if (rfqStatusInfoList?.length > 0) {
			const baseRfq = rfqStatusInfoList?.find((rfq) => rfq.isBaseRfq);
			if (baseRfq) {
				setSelectedETD(baseRfq.rfqId);
			}
		}
	}, [rfqStatusInfoList]);

	return (
		<LoaderComponent loading={loading}>
			<div className="tna-container bg-gray">
				{isShowProducts && (
					<div className="tna-tab-container scroll-x-label p-0 mt-2">
						<TnaStylesList
							tnaProductList={tnaProductList}
							selectedStyle={selectedStyle}
							onSelectProduct={onSelectProduct}
							setSelectedStyle={setSelectedStyle}
							setIsSelectedButtonClicked={setIsSelectedButtonClicked}
							orderId={orderId}
							setLoading={setLoading}
							handleExcelFileUpload={handleExcelFileUpload}
							onStartOrder={handleStartOrder}
							getPendingProductList={getPendingProductList}
							listOfAllTnaProduct={listOfAllTnaProduct}
							setListOfAllTnaProduct={setListOfAllTnaProduct}
							rfqStatusInfoList={rfqStatusInfoList}
							selectedETD={selectedETD}
						/>
					</div>
				)}

				{!isSelecteButtonClicked && (
					<div className="tna-task-container">
						<div className="create-tna-tasks mb-20 pb-0">
							<div className="tab-and-date mb-3 d-flex justify-content-between align-items-center">
								<div className="inner-tabs pl-12 pt-12">
									{isTnaHasSetProduct && (
										<SetProductsTabButtons
											activeSetMemberId={activeSetMemberId}
											onChangeTabOfSetMember={onChangeTabOfSetMember}
											productsOfSet={productsOfSet}
											submittedSetMembers={submittedSetMembers}
											isCompleated={isCompleated}
										/>
									)}
								</div>
								<EtdDetails
									selectedStyle={selectedStyle}
									selectedStyleDetails={selectedStyleDetails}
								/>
							</div>
							{selectedStyle && (
								<div>
									<div className="d-flex justify-content-between align-items-center">
										<div className="added-task">
											<div className="d-flex align-items-center">
												<CreateDuplicateTNA
													orderId={orderId}
													tnaProductList={
														requestFrom === 'TNA_RUNNING'
															? runningProductList
															: tnaProductList
													}
													onSelectPlanForDuplication={onSelectPlanForDuplication}
													isExcelFileSubmitted={isExcelFileSubmitted}
												/>
												<DuplicateTnaConfirmModal
													plan={planToDuplicate}
													loading={loading}
													onCancel={() => setPlanToDuplicate(null)}
													onConfirm={onConfirmDuplication}
												/>
												{renderClearButton()}

												<AddNewTaskModal
													errors={errors}
													handleAddTaskFormChange={handleAddTaskFormChange}
													loading={loading}
													onModalCancel={onModalCancel}
													onSubmit={onSubmit}
													showModal={showModal}
													stageTaskList={stageTaskList}
													taskFormInputData={taskFormInputData}
												/>
											</div>
										</div>
									</div>
								</div>
							)}
						</div>
						<div className="common-accordion">
							<div className="accordion" id="accordionExample">
								<TNAAccordian
									id="One"
									title="Pre Production"
									expand={selectedProduct?.isBaseRfq}
								>
									{selectedStyle && (
										<div className="tna-task-table">
											<table className="table with-border">
												<thead>
													<tr>
														<th>SL</th>
														<th>TNA Task Name</th>
														<th>Due date</th>
													</tr>
												</thead>
												<tbody>
													{selectedStyle &&
														renderDeliverableName(
															[
																'ORDER_PLACED',
																'MATERIALS',
																'SAMPLING',
																'PP_ACTIVITIES'
															],
															'PRE_PRODUCTION'
														)}
												</tbody>
											</table>
										</div>
									)}
								</TNAAccordian>
								<TNAAccordian id="Two" title="Production" expand={true}>
									{selectedStyle && (
										<div className="tna-task-table">
											<table className="table with-border">
												<thead>
													<tr>
														<th>SL</th>
														<th>TNA Task Name</th>
														<th>Due date</th>
													</tr>
												</thead>
												<tbody>
													{selectedStyle && renderDeliverableName([], 'PRODUCTION')}
												</tbody>
											</table>
										</div>
									)}
								</TNAAccordian>
							</div>
						</div>
					</div>
				)}
				{selectedStyle && (
					<div className="py-3 d-flex justify-content-end">
						{/* A style now opens on its default template, so the spreadsheet route stays
						    reachable here rather than only while the plan already came from a file. */}
						{!isExcelFileSubmitted() && (
							<>
								<div className="inline-upload mr-3 cursor-pointer">
									<label htmlFor="tna-file-upload" className="cursor-pointer">
										Upload
									</label>
									<input
										id="tna-file-upload"
										type="file"
										accept=".xlsx, .xls"
										className="file-upload d-none"
										onChange={(e) => handleExcelFileUpload(e)}
									/>
								</div>

								<button className="button size36 outline mr-3" onClick={handleDownloadFile}>
									Download
								</button>
							</>
						)}
						{!isUsingExcel && (
							<button className="button size36" onClick={updatedTnaSubmit}>
								Submit
							</button>
						)}{' '}
						{/*{requestFrom === 'TNA_RUNNING' &&*/}
						{/*	!selectedTemplateDays &&*/}
						{/*	selectedStyleDetailsFromRunning?.planningStatus === PLANNING_STATUS.SUBMITTED && (*/}
						{/*		<button className="button size36" onClick={createTna}>*/}
						{/*			Create TNA*/}
						{/*		</button>*/}
						{/*	)}*/}
					</div>
				)}
			</div>
		</LoaderComponent>
	);
};

export default CreateTna;
